/**
 * Evolução de um sistema de nuvens: a série regular para os gráficos da ficha
 * e a tendência (intensificando ou não) — funções puras, testadas em
 * `evolucao.test.ts`.
 *
 * Tudo sai das colunas que o pyForTraCC publica: a área (`km2`), a expansão
 * normalizada dela (`exp`, 10⁻⁶ s⁻¹) e as temperaturas de brilho mínima e
 * média (`tmin`, `tmed`, K). Nada é convertido nem suavizado além da média
 * da janela da tendência.
 */
import { MINUTE_MS } from '../lib/time';
import type { Historico, Sistema } from './layer';

/** Cadência do rastreio quando a história não a revela (um ponto só). */
export const PASSO_PADRAO_MIN = 10;
/** Janela da tendência: três imagens. Uma só oscila com a borda do limiar; uma hora já é passado. */
export const JANELA_MIN = 30;

/**
 * Expansão média a partir da qual a área "cresce" ou "encolhe".
 * 50 × 10⁻⁶ s⁻¹ ≈ 3% a cada 10 min (≈ 9% em 30 min). Abaixo disso é ruído de
 * borda: num sistema de 100 pixels, 1–2 pixels a mais dão 17–33.
 */
export const LIMIAR_EXP = 50;
/** O mesmo limiar em variação da área na janela, para quando a fonte não traz `exp`. */
export const LIMIAR_AREA_PCT = 9;
/** Temperatura mínima: é UM pixel, então pede 2 K (o canal 13 tem ruído de ~0,1 K, mas o pixel mais frio troca de lugar). */
export const LIMIAR_TMIN_K = 2;
/** Temperatura média: milhares de pixels, 1 K já é mudança do sistema inteiro. */
export const LIMIAR_TMED_K = 1;

export type Classe = 'intensificando' | 'estavel' | 'enfraquecendo' | 'sem-dados';

export const NOME_CLASSE: Readonly<Record<Classe, string>> = {
  intensificando: 'Intensificando',
  estavel: 'Estável',
  enfraquecendo: 'Enfraquecendo',
  'sem-dados': 'Sem tendência ainda',
};

export interface Tendencia {
  classe: Classe;
  /** Minutos cobertos pela comparação (0 sem história). */
  minutos: number;
  /** Expansão média na janela (10⁻⁶ s⁻¹), ou null. */
  exp: number | null;
  /** Variação da área na janela (%), ou null. */
  areaPct: number | null;
  /** Variação da temperatura mínima e da média na janela (K; negativa = esfriou), ou null. */
  dTmin: number | null;
  dTmed: number | null;
  /** −1 encolhe/esquenta · 0 parado · +1 cresce/esfria. */
  sinalArea: -1 | 0 | 1;
  sinalTopo: -1 | 0 | 1;
}

const ehNumero = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function media(valores: readonly (number | null)[]): number | null {
  const v = valores.filter(ehNumero);
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
}

const sinal = (v: number | null, limiar: number): -1 | 0 | 1 => (v === null ? 0 : v >= limiar ? 1 : v <= -limiar ? -1 : 0);

/** Diferença entre o último valor e o primeiro medido da janela; null sem os dois. */
function variacao(serie: readonly (number | null)[], desde: number): { de: number; para: number } | null {
  const para = serie[serie.length - 1];
  if (!ehNumero(para)) return null;
  for (let i = desde; i < serie.length - 1; i++) {
    const de = serie[i];
    if (ehNumero(de)) return { de, para };
  }
  return null;
}

/**
 * A tendência do sistema no quadro.
 *
 * Dois sinais independentes, cada um vale −1, 0 ou +1:
 * - **área**: a expansão média da janela (ou, sem ela, a variação da área);
 * - **topo**: a temperatura mínima e a média — esfriar é a nuvem subindo.
 *   Se uma esfria e a outra esquenta, o topo não diz nada (0).
 *
 * A soma decide: positiva intensifica, negativa enfraquece, zero é estável
 * (inclusive "cresce mas esquenta": bigorna se espalhando, não intensificação).
 */
export function tendencia(s: Pick<Sistema, 'exp' | 'hist'>, janelaMin: number = JANELA_MIN): Tendencia {
  const h = s.hist;
  const vazia: Tendencia = { classe: 'sem-dados', minutos: 0, exp: null, areaPct: null, dTmin: null, dTmed: null, sinalArea: 0, sinalTopo: 0 };
  if (!h || h.dt.length < 2) {
    // Sem história, a expansão do próprio quadro (uma imagem contra a anterior) é o que há.
    if (!ehNumero(s.exp)) return vazia;
    const sinalArea = sinal(s.exp, LIMIAR_EXP);
    return { ...vazia, classe: classeDe(sinalArea, 0), minutos: PASSO_PADRAO_MIN, exp: s.exp, sinalArea };
  }
  const n = h.dt.length;
  let desde = n - 1;
  while (desde > 0 && h.dt[desde - 1]! >= -janelaMin) desde--;
  // A janela precisa de um ponto anterior ao quadro; com buraco na série, o último que houver.
  if (desde === n - 1) desde = n - 2;
  const minutos = Math.round(-h.dt[desde]!);

  // `exp` de cada ponto compara com o ANTERIOR: o do início da janela olha para fora dela.
  const exp = media(h.exp.slice(desde + 1)) ?? (ehNumero(s.exp) ? s.exp : null);
  const area = variacao(h.km2, desde);
  const areaPct = area && area.de > 0 ? ((area.para - area.de) / area.de) * 100 : null;
  const tmin = variacao(h.tmin, desde);
  const tmed = variacao(h.tmed, desde);
  const dTmin = tmin ? tmin.para - tmin.de : null;
  const dTmed = tmed ? tmed.para - tmed.de : null;

  const sinalArea = exp !== null ? sinal(exp, LIMIAR_EXP) : sinal(areaPct, LIMIAR_AREA_PCT);
  // Esfriar (variação negativa) é sinal positivo.
  const soma = -sinal(dTmin, LIMIAR_TMIN_K) - sinal(dTmed, LIMIAR_TMED_K);
  const sinalTopo: -1 | 0 | 1 = soma > 0 ? 1 : soma < 0 ? -1 : 0;

  if (exp === null && areaPct === null && dTmin === null && dTmed === null) return { ...vazia, minutos };
  return { classe: classeDe(sinalArea, sinalTopo), minutos, exp, areaPct, dTmin, dTmed, sinalArea, sinalTopo };
}

function classeDe(area: number, topo: number): Classe {
  const soma = area + topo;
  return soma > 0 ? 'intensificando' : soma < 0 ? 'enfraquecendo' : 'estavel';
}

const comSinal = (v: number, casas = 0) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(casas).replace('.', ',')}`;

/** O que sustenta o selo, em poucas palavras: "área +18% em 30 min · topo 3 K mais frio". */
export function motivos(t: Tendencia): string[] {
  const out: string[] = [];
  if (t.areaPct !== null && t.minutos > 0) out.push(`área ${comSinal(Math.round(t.areaPct))}% em ${t.minutos} min`);
  else if (t.exp !== null) out.push(`expansão ${comSinal(Math.round(t.exp))}`);
  if (t.dTmin !== null && Math.abs(t.dTmin) >= 0.5) {
    out.push(`topo ${Math.abs(t.dTmin).toFixed(1).replace('.', ',')} K mais ${t.dTmin < 0 ? 'frio' : 'quente'}`);
  } else if (t.dTmed !== null && Math.abs(t.dTmed) >= 0.5) {
    out.push(`média ${Math.abs(t.dTmed).toFixed(1).replace('.', ',')} K mais ${t.dTmed < 0 ? 'fria' : 'quente'}`);
  } else if (t.dTmin !== null) {
    out.push('topo sem mudança');
  }
  return out;
}

// ---------------------------------------------------------------------------
// Série regular para os gráficos
// ---------------------------------------------------------------------------

export interface SerieRegular {
  /** Instante de cada fatia (epoch ms), em passos iguais; o último é o quadro. */
  instantes: number[];
  passoMin: number;
  km2: (number | null)[];
  tmin: (number | null)[];
  tmed: (number | null)[];
  exp: (number | null)[];
}

/** O menor intervalo entre pontos vizinhos (a cadência), entre 1 e 60 min. */
export function passoDe(dt: readonly number[]): number {
  let menor = Infinity;
  for (let i = 1; i < dt.length; i++) menor = Math.min(menor, dt[i]! - dt[i - 1]!);
  return Number.isFinite(menor) && menor >= 1 && menor <= 60 ? menor : PASSO_PADRAO_MIN;
}

/**
 * A história em fatias de largura igual (o que a moldura dos gráficos pede):
 * uma imagem que faltou vira um buraco (`null`), não uma reta ligando os vizinhos.
 * `null` quando não há dois pontos.
 */
export function serieRegular(hist: Historico | null, t: number, maxPontos = 72): SerieRegular | null {
  if (!hist || hist.dt.length < 2) return null;
  const passoMin = passoDe(hist.dt);
  const n = Math.min(maxPontos, Math.round(-hist.dt[0]! / passoMin) + 1);
  if (n < 2) return null;
  const vazio = () => Array.from<number | null>({ length: n }).fill(null);
  const out: SerieRegular = {
    instantes: Array.from({ length: n }, (_, i) => t - (n - 1 - i) * passoMin * MINUTE_MS),
    passoMin,
    km2: vazio(),
    tmin: vazio(),
    tmed: vazio(),
    exp: vazio(),
  };
  hist.dt.forEach((dt, k) => {
    const i = n - 1 + Math.round(dt / passoMin);
    if (i < 0 || i >= n) return;
    out.km2[i] = hist.km2[k] ?? null;
    out.tmin[i] = hist.tmin[k] ?? null;
    out.tmed[i] = hist.tmed[k] ?? null;
    out.exp[i] = hist.exp[k] ?? null;
  });
  return out;
}

export interface Faixa {
  min: number;
  max: number;
  base: number;
  amplitude: number;
}

/**
 * Faixa vertical comum a uma ou mais séries. `piso` é a menor amplitude
 * aceita: sem ele, uma série quase constante vira um serrote de ruído.
 */
export function faixa(series: readonly (readonly (number | null)[])[], piso: number): Faixa | null {
  const v = series.flat().filter(ehNumero);
  if (!v.length) return null;
  const min = Math.min(...v);
  const max = Math.max(...v);
  const amplitude = Math.max(max - min, piso);
  return { min, max, base: min - (amplitude - (max - min)) / 2, amplitude };
}

/** Trechos contínuos (índices) de medições: a linha não atravessa buracos. */
export function trechos(serie: readonly (number | null)[]): number[][] {
  const out: number[][] = [];
  let atual: number[] = [];
  serie.forEach((v, i) => {
    if (ehNumero(v)) atual.push(i);
    else if (atual.length) {
      out.push(atual);
      atual = [];
    }
  });
  if (atual.length) out.push(atual);
  return out;
}
